// src/mobile_pages/contract/Sub_view.jsx

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSubContractDetail, updateSubContract } from "../../api/contract/sub";
import SidePanel from "../../components/common/layout/SidePanel";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";
import MobileShareButton from "../../components/common/mobile/MobileShareButton";
import FileList from "../../components/common/papers/FileList";
import ActivitySearch from "../../components/const/Photo/ActivitySearch";
import { softDeleteDoc } from "../../api/papers";

const emptyMeta = {
  title: "",
  contract_code: "",
  confirmer_company_name: "",
  revision: 1,
  version: 1,
  construction_type: "",
  construction_type_detail: "",
  status: "",
  amount: "",
  memo: "",
  activity_ids: [],
};

const CONSTRUCTION_TYPE_DETAIL_LABELS = {
  GENERAL: "일반",
  ARCHITECTURAL: "건축",
  STRUCTURAL: "구조",
  CIVIL: "토목",
  LANDSCAPING: "조경",
  MECHANICAL: "기계",
  ELECTRICAL: "전기",
  COMMUNICATION: "통신",
  FIREFIGHTING: "소방",
  FIREFIGHTING_MECHANICAL: "소방 기계",
  FIREFIGHTING_ELECTRICAL: "소방 전기",
  INTERIOR: "인테리어",
  OTHERS: "기타",
};

const STATUS_LABELS = {
  DRAFT: "임시",
  FOR_INFORMATION: "정보",
  FOR_REVIEW: "검토",
  FOR_APPROVAL: "승인",
  FINAL: "최종",
  AS_BUILT: "준공",
  NO_LONGER_IN_USE: "폐기",
};

const normalize = (data) => {
  const safe = data || {};
  return {
    dns: safe.dns || "",
    title: safe.title || "",
    created_at: safe.created_at || null,
    created_by: safe.created_by || "",
    versions: safe.versions || [],
    status_history: safe.status_history || [],
    meta: { ...emptyMeta, ...(safe.meta || {}) },
  };
};

const Sub_view = ({ contractId: propContractId, onDeleted, onUpdated, onClose, inBottomSheet }) => {
  const { contractId: routeContractId } = useParams();
  const navigate = useNavigate();
  const contractId = propContractId || routeContractId;

  const [contract, setContract] = useState(() => normalize({}));
  const [form, setForm] = useState(() => normalize({}));
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [editing, setEditing] = useState(false);

  const [loadedFiles, setLoadedFiles] = useState([]);
  const attachmentRef = useRef(null);

  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [activitySearchOpen, setActivitySearchOpen] = useState(false);

  const fetchDetail = useCallback(async (id = contractId) => {
    if (!id) return;
    setLoading(true);
    setErrMsg("");

    try {
      const data = await getSubContractDetail(id);
      const norm = normalize(data);
      setContract(norm);
      setForm(norm);
    } catch (err) {
      console.error("계약 상세 불러오기 실패:", err);
      setErrMsg("계약 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleDelete = async () => {
    if (!window.confirm("해당 하도급 계약과 관련된 문서가 삭제됩니다. 계속하시겠습니까?")) return;

    try {
      const dnsList = [contract.dns];
      if (contract.versions?.length) {
        dnsList.push(...contract.versions.map((v) => v.dns));
      }
      await Promise.all(dnsList.map((dns) => softDeleteDoc(dns, true)));

      alert("삭제 완료");
      onDeleted?.();
      onClose?.();
      if (!propContractId) navigate(-1);
    } catch (err) {
      console.error(err);
      alert("삭제 실패");
    }
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, [name]: value },
    }));
  };

  const handleTitleChange = (e) => {
    const { value } = e.target;
    setForm((prev) => ({
      ...prev,
      meta: { ...prev.meta, title: value },
    }));
  };

  const handleSave = async () => {
    try {
      const fd = new FormData();
      fd.append("meta", JSON.stringify(form.meta));
      fd.append("title", form.meta.title);

      const res = await updateSubContract(contractId, fd);
      await attachmentRef.current?.save(res.dns);

      alert("수정되었습니다.");
      setEditing(false);

      const newDns = res?.dns ?? contractId;
      await fetchDetail(newDns);
      onUpdated?.(newDns);
    } catch (err) {
      console.error("수정 실패:", err);
      alert("수정 실패 다시 시도해주세요");
    }
  };

  const versions = useMemo(() => contract?.versions ?? [], [contract]);
  const meta = contract.meta;

  // 필드 컴포넌트
  const Field = ({ label, children, value }) => (
    <div className="space-y-1">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      {children ?? <div className="text-sm text-gray-900 break-words">{value || "-"}</div>}
    </div>
  );

  if (!contractId) {
    return <div className="p-6 text-center text-gray-500 text-sm">계약 ID 없음</div>;
  }

  if (loading) {
    return inBottomSheet ? (
      <div className="space-y-4 animate-pulse p-4">
        <div className="h-6 bg-gray-200 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-4 bg-gray-100 rounded w-2/3" />
        <div className="h-32 bg-gray-100 rounded" />
      </div>
    ) : (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3 animate-pulse p-4">
          <div className="h-6 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-100 rounded w-3/4" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (errMsg) {
    return inBottomSheet ? (
      <div className="space-y-4">
        <div className="p-6 text-center text-red-500 text-sm">{errMsg}</div>
      </div>
    ) : (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3">
          <div className="p-6 text-center text-red-500 text-sm">{errMsg}</div>
        </div>
      </div>
    );
  }

  const mainContent = (
    <>
      {/* 액션 버튼 영역 */}
      <div className="flex items-center justify-between gap-2 min-w-0 overflow-hidden">
        <button
          onClick={() => setShowVersionPanel(true)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium"
        >
          Rev.{meta.revision} v{meta.version}
        </button>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                className="btn-save-sm inline-flex items-center gap-1"
              >
                저장
              </button>
              <button
                onClick={() => { setEditing(false); setForm(contract); }}
                className="btn-cancel-sm inline-flex items-center gap-1"
              >
                취소
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="btn-edit-sm inline-flex items-center gap-1"
              >
                수정
              </button>
              <button
                onClick={handleDelete}
                className="btn-delete-sm inline-flex items-center gap-1"
              >
                삭제
              </button>
              <MobileShareButton
                title={meta?.title || "하도급 계약"}
                className="btn-basic-sm"
              />
            </>
          )}
        </div>
      </div>

      {/* 기본 정보 */}
      <div className="rounded-lg border bg-white p-3 space-y-4 overflow-hidden">
        {/* 계약명 */}
        <Field label="계약명">
          {editing ? (
            <input
              type="text"
              value={form.meta.title || ""}
              onChange={handleTitleChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          ) : (
            <div className="text-base font-semibold text-gray-900 break-words">{meta.title || "-"}</div>
          )}
        </Field>

        {/* 2열 그리드 */}
        <div className="grid grid-cols-2 gap-3 overflow-hidden">
          <Field label="계약 코드">
            {editing ? (
              <input
                type="text"
                name="contract_code"
                value={form.meta.contract_code || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
              />
            ) : (
              <span className="text-sm break-all">{meta.contract_code || "-"}</span>
            )}
          </Field>

          <Field label="상태">
            {editing ? (
              <select
                name="status"
                value={form.meta.status || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              >
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            ) : (
              <span className="text-sm">{STATUS_LABELS[meta.status] || "-"}</span>
            )}
          </Field>

          <Field label="공사 구분">
            {editing ? (
              <select
                name="construction_type"
                value={form.meta.construction_type || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              >
                <option value="본공사">본공사</option>
                <option value="전기공사">전기공사</option>
                <option value="통신공사">통신공사</option>
                <option value="소방공사">소방공사</option>
              </select>
            ) : (
              <span className="text-sm">{meta.construction_type || "-"}</span>
            )}
          </Field>

          <Field label="세부 공종">
            {editing ? (
              <select
                name="construction_type_detail"
                value={form.meta.construction_type_detail || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              >
                {Object.entries(CONSTRUCTION_TYPE_DETAIL_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            ) : (
              <span className="text-sm">{CONSTRUCTION_TYPE_DETAIL_LABELS[meta.construction_type_detail] || "-"}</span>
            )}
          </Field>

          <Field label="금액">
            {editing ? (
              <input
                type="number"
                name="amount"
                value={form.meta.amount || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-right"
              />
            ) : (
              <span className="text-sm">{meta.amount ? `${Number(meta.amount).toLocaleString("ko-KR")}원` : "-"}</span>
            )}
          </Field>

          <Field label="주계약업체">
            {editing ? (
              <input
                type="text"
                name="confirmer_company_name"
                value={form.meta.confirmer_company_name || ""}
                onChange={handleMetaChange}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
              />
            ) : (
              <span className="text-sm break-words">{meta.confirmer_company_name || "-"}</span>
            )}
          </Field>

          <Field label="작성자" value={contract.created_by} />

          <Field label="생성일">
            <span className="text-sm">
              {contract.created_at
                ? new Date(contract.created_at).toLocaleDateString("ko-KR")
                : "-"}
            </span>
          </Field>
        </div>

        {/* 특이사항 */}
        <Field label="특이사항">
          {editing ? (
            <textarea
              name="memo"
              value={form.meta.memo || ""}
              onChange={handleMetaChange}
              className="w-full h-24 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
            />
          ) : (
            <div className="text-sm text-gray-700 bg-white rounded-lg p-3 min-h-[60px] whitespace-pre-line break-words">
              {meta.memo || "-"}
            </div>
          )}
        </Field>

        {/* Activity 연결 */}
        <Field label="연결된 Activity">
          {editing ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setActivitySearchOpen(true)}
                className="btn-search-sm"
              >
                Activity 검색
              </button>
              <div className="text-xs text-gray-600">
                {form.meta.activity_ids?.length
                  ? form.meta.activity_ids.map((id) => <div key={id}>Activity #{id}</div>)
                  : "등록된 활동 없음"}
              </div>
              <ActivitySearch
                open={activitySearchOpen}
                onClose={() => setActivitySearchOpen(false)}
                onSelect={(activities) => {
                  const ids = activities.map((a) => a.id);
                  setForm((prev) => ({
                    ...prev,
                    meta: { ...prev.meta, activity_ids: ids },
                  }));
                }}
              />
            </div>
          ) : (
            <div className="text-xs text-gray-600">
              {meta.activity_ids?.length
                ? meta.activity_ids.map((id) => <div key={id}>Activity #{id}</div>)
                : "등록된 활동 없음"}
            </div>
          )}
        </Field>
      </div>

      {/* 첨부파일 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-xs font-medium text-gray-500 mb-2">첨부파일</div>
        {editing ? (
          <MobileAttachmentBox ref={attachmentRef} initial={loadedFiles} />
        ) : (
          <FileList
            dns={contract.dns}
            onFilesLoaded={(files) => {
              const mapped = files.map((f) => ({
                _type: "library",
                id: f.file_id || f.id,
                name: f.name,
              }));
              setLoadedFiles(mapped);
            }}
          />
        )}
      </div>

      {/* 수정 내역 사이드 패널 */}
      <SidePanel
        isOpen={showVersionPanel}
        onClose={() => setShowVersionPanel(false)}
        title="수정 내역"
        width={320}
        side="left"
      >
        <div className="space-y-2 p-2">
          {versions.length > 0 ? (
            versions.map((v, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">
                    Rev.{v.revision} v{v.version}
                  </span>
                  {v.is_current && (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700">
                      최신
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-900 truncate">{v.title}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {v.created_by} · {v.created_at ? new Date(v.created_at).toLocaleDateString("ko-KR") : "-"}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-sm text-gray-500 py-8">
              수정 내역이 없습니다.
            </div>
          )}
        </div>
      </SidePanel>
    </>
  );

  if (inBottomSheet) {
    return <div className="space-y-4 min-w-0 overflow-hidden">{mainContent}</div>;
  }

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {mainContent}
      </div>
    </div>
  );
};

export default Sub_view;
